import { useState, useRef } from 'react'

const ACCENT = '#8b6cff'
const BG = '#16171a'
const LINE = '#ffffff0e'
const FG = '#ececef'
const FG2 = '#a8aab2'
const FG3 = '#6e7079'

const BONES = [
  { id: 'root',        name: 'root',        depth: 0 },
  { id: 'hips',        name: 'hips',        depth: 1 },
  { id: 'spine',       name: 'spine',       depth: 2 },
  { id: 'spine_01',    name: 'spine.01',    depth: 3 },
  { id: 'neck',        name: 'neck',        depth: 3 },
  { id: 'head',        name: 'head',        depth: 4 },
  { id: 'L_shoulder',  name: 'shoulder.L',  depth: 3 },
  { id: 'L_upper_arm', name: 'upper_arm.L', depth: 4 },
  { id: 'L_lower_arm', name: 'lower_arm.L', depth: 5 },
  { id: 'L_hand',      name: 'hand.L',      depth: 6 },
  { id: 'R_shoulder',  name: 'shoulder.R',  depth: 3 },
  { id: 'R_upper_arm', name: 'upper_arm.R', depth: 4 },
  { id: 'R_lower_arm', name: 'lower_arm.R', depth: 5 },
  { id: 'R_hand',      name: 'hand.R',      depth: 6 },
  { id: 'L_thigh',     name: 'thigh.L',     depth: 2 },
  { id: 'R_thigh',     name: 'thigh.R',     depth: 2 },
]

function TimelineTrack({ bone, totalFrames = 72 }) {
  const seed = bone.id.length * 31 + 7
  const keys = Array.from({ length: 4 + (seed % 5) }, (_, i) => {
    const r = ((seed * (i + 1) * 9301 + 49297) % 233280) / 233280
    return Math.round(r * totalFrames)
  }).sort((a, b) => a - b)
  return (
    <div style={{ height: 18, position: 'relative', background: '#0e0f13', borderRadius: 2, overflow: 'hidden', flex: 1 }}>
      {keys.map((frame, i) => (
        <div key={i} style={{
          position: 'absolute', top: '50%', left: `${(frame / totalFrames) * 100}%`,
          transform: 'translate(-50%,-50%) rotate(45deg)',
          width: 6, height: 6, background: ACCENT, borderRadius: 1,
        }} />
      ))}
    </div>
  )
}

export function AnimatorEditorPanel() {
  const [selectedBone, setSelectedBone] = useState('hips')
  const [bottomMode, setBottomMode] = useState('dope')
  const [playing, setPlaying] = useState(false)
  const uploadRef = useRef(null)

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: BG, fontFamily: 'var(--m-font)', color: FG, overflow: 'hidden' }}>

      {/* Top bar */}
      <div style={{ height: 44, flexShrink: 0, background: '#1c1d22', borderBottom: `1px solid ${LINE}`, display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12 }}>
        <span style={{ fontFamily: 'var(--m-font)', fontSize: 11, letterSpacing: '.18em', color: ACCENT + 'cc', fontWeight: 500, textTransform: 'uppercase' }}>Animator</span>
        <span style={{ fontFamily: 'var(--m-mono)', fontSize: 11.5, color: FG2 }}>idle_balanced</span>
        <span style={{ fontFamily: 'var(--m-mono)', fontSize: 10, color: FG3, padding: '1px 6px', border: `1px solid #ffffff18`, borderRadius: 3 }}>2.40s · 30fps</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={() => uploadRef.current?.click()} style={{
            padding: '5px 12px', borderRadius: 5, fontSize: 11, background: 'transparent',
            border: `1px solid #ffffff18`, color: FG2, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <svg viewBox="0 0 16 16" width="10" height="10" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round"><path d="M8 11V3M5 6l3-3 3 3M3 13h10"/></svg>
            Replace .fbx
          </button>
          <button style={{ padding: '5px 12px', borderRadius: 5, fontSize: 11, background: 'transparent', border: `1px solid #ffffff18`, color: FG2, cursor: 'pointer' }}>Export</button>
          <button style={{ padding: '5px 12px', borderRadius: 5, fontSize: 11, background: ACCENT, border: `1px solid ${ACCENT}`, color: '#fff', cursor: 'pointer' }}>Save</button>
        </div>
      </div>

      {/* Main body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

        {/* Left: bone tree */}
        <div style={{ width: 200, flexShrink: 0, borderRight: `1px solid ${LINE}`, background: '#1a1b1f', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '7px 10px 5px', fontFamily: 'var(--m-font)', fontSize: 9.5, letterSpacing: '.14em', color: FG3, textTransform: 'uppercase', borderBottom: `1px solid ${LINE}` }}>Bones</div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {BONES.map(b => (
              <button key={b.id} onClick={() => setSelectedBone(b.id)} style={{
                width: '100%', textAlign: 'left',
                padding: `4px 8px 4px ${8 + b.depth * 10}px`,
                background: selectedBone === b.id ? ACCENT + '18' : 'transparent',
                border: 'none', borderLeft: `2px solid ${selectedBone === b.id ? ACCENT : 'transparent'}`,
                color: selectedBone === b.id ? FG : FG2,
                fontSize: 11, fontFamily: 'var(--m-font)', cursor: 'pointer',
              }}>{b.depth > 0 ? '└ ' : ''}{b.name}</button>
            ))}
          </div>
        </div>

        {/* Center */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

          {/* Viewport */}
          <div style={{ flex: '0 0 140px', background: 'radial-gradient(ellipse at 50% 65%, #1a2238 0%, #0a0d14 70%, #05060a 100%)', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: `1px solid ${LINE}` }}>
            <div style={{ fontFamily: 'var(--m-font)', fontSize: 11, color: FG3 }}>Animation preview</div>
            <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6 }}>
              {[
                ['⏮', () => {}],
                [playing ? '⏸' : '▶', () => setPlaying(p => !p)],
                ['⏹', () => setPlaying(false)],
              ].map(([icon, fn], i) => (
                <button key={i} onClick={fn} style={{
                  background: '#1a1b1f', border: `1px solid #ffffff18`, color: FG2,
                  borderRadius: 4, width: 26, height: 22, cursor: 'pointer', fontSize: 10,
                }}>{icon}</button>
              ))}
              <span style={{ fontFamily: 'var(--m-mono)', fontSize: 9.5, color: FG3, alignSelf: 'center', paddingLeft: 4 }}>0 / 72</span>
            </div>
          </div>

          {/* Timeline tracks */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <div style={{ height: 18, flexShrink: 0, background: '#13141a', borderBottom: `1px solid ${LINE}`, position: 'relative', padding: '0 8px' }}>
              {[0, 0.5, 1.0, 1.5, 2.0].map(t => (
                <span key={t} style={{ position: 'absolute', left: `calc(${(t / 2.4) * 100}% + 8px)`, fontFamily: 'var(--m-mono)', fontSize: 9, color: FG3, top: 3 }}>{t.toFixed(1)}s</span>
              ))}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {BONES.slice(0, 10).map(b => (
                <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 80, fontSize: 9.5, fontFamily: 'var(--m-font)', color: selectedBone === b.id ? ACCENT + 'cc' : FG3, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
                  <TimelineTrack bone={b} />
                </div>
              ))}
            </div>
          </div>

          {/* Bottom: dope sheet / curves */}
          <div style={{ height: 70, flexShrink: 0, borderTop: `1px solid ${LINE}`, background: '#202126' }}>
            <div style={{ display: 'flex', gap: 4, padding: '5px 8px', borderBottom: `1px solid ${LINE}` }}>
              {[['dope', 'Dope Sheet'], ['curves', 'Curves']].map(([id, label]) => (
                <button key={id} onClick={() => setBottomMode(id)} style={{
                  padding: '2px 8px', fontSize: 10, borderRadius: 3,
                  background: bottomMode === id ? ACCENT + '22' : 'transparent',
                  border: `1px solid ${bottomMode === id ? ACCENT : '#ffffff18'}`,
                  color: bottomMode === id ? ACCENT + 'cc' : FG3,
                  cursor: 'pointer', fontFamily: 'var(--m-font)',
                }}>{label}</button>
              ))}
            </div>
            <div style={{ padding: '8px', fontFamily: 'var(--m-font)', fontSize: 10, color: FG3 }}>
              {bottomMode === 'curves' ? 'Curve editor — coming soon' : 'Dope sheet — coming soon'}
            </div>
          </div>
        </div>
      </div>

      <input ref={uploadRef} type="file" accept=".fbx,.glb" style={{ display: 'none' }} onChange={() => {}} />
    </div>
  )
}
